import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
	unassignTask as unassignTaskService,
	assignTask as assignTaskService,
	activateTaskStatus,
	deactivateTaskStatus,
	getWorkItemInfo,
	getForemanBacklog,
	getForemanBacklogRoots,
	getForemanBacklogChildren,
} from '../../services/backlogService'
import { Employee } from '../../types/Employee/Employee'
import { ReworkTask, WorkItemTask } from '../../types/Work/WorkItemTask'
import { WorkItemInfo } from '../../types/Work/WorkItemInfo'
import { WorkItem } from '../../types/Work/WorkItem'
import { revertSignOut, signInWorkerByTask } from '../../services/attendanceService'
import { findLeafWorkItems, searchWorkItemsHierarchy } from '../../utils/WorkItemsTreeUtils'
import { useIndexedDB } from '../hooks/useIndexedDB'
import { useLazyWorkItemTree } from '../core/lazyTree/useLazyWorkItemTree'

interface BacklogContextType {
	employee: Employee | undefined
	directReports: Employee[] | undefined
	fullTree: boolean
	expandedIds: Set<number>
	childrenById: Map<number, WorkItem[]>
	loadingChildrenIds: Set<number>
	failedChildrenIds: Set<number>
	toggleWorkItem: (workItem: WorkItem) => void
	retryChildren: (workItemId: number) => void
	expandWorkItems: (workItemIds: number[]) => Promise<void>
	unassignTask: (
		task: WorkItemTask | ReworkTask,
		employee: Employee,
		taskType: string,
	) => Promise<boolean>
	assignTask: (
		task: WorkItemTask | ReworkTask,
		employee: Employee,
		taskType: string,
		workItem?: WorkItem,
	) => Promise<boolean | 'duplicate'>
	loadData: () => Promise<void>
	setTaskStatus: (
		changeToActive: boolean,
		workerId: number,
		taskId: number,
		taskType: string,
	) => Promise<boolean>
	getWorkItemPaths: (workItemId: number[]) => Promise<WorkItemInfo[]>
	autoSignInWorker: (
		directReportId: number,
		taskId: number,
		taskType: string,
	) => Promise<Employee | null>
	revertLaborSignOut: (workerId: number) => Promise<Employee | null>
	openToolsMenu: boolean
	setOpenToolsMenu: (open: boolean) => void
	toolsMenuStep: number
	setToolsMenuStep: (step: number) => void
	setForemanId: React.Dispatch<React.SetStateAction<number | undefined>>
	topSectionLoading: boolean
	bottomSectionLoading: boolean
	searchText: string
	setSearchText: React.Dispatch<React.SetStateAction<string>>
}

const BacklogContext = createContext<BacklogContextType | null>(null)

const useBacklogContext = (): BacklogContextType => {
	const backlogContextState = useContext(BacklogContext)
	if (backlogContextState === null) {
		throw new Error(
			'BacklogContext not found. Try wrapping a parent component with <BacklogContext.Provider>.',
		)
	}
	return backlogContextState
}

type BacklogProviderProps = {
	children?: React.ReactNode
}

const BacklogProvider = ({ children }: BacklogProviderProps) => {
	const [foremanId, setForemanId] = useState<number | undefined>()
	const [employee, setEmployee] = useState<Employee | undefined>()
	const [directReports, setDirectReports] = useState<Employee[] | undefined>([])
	const [openToolsMenu, setOpenToolsMenu] = useState(false)
	const [toolsMenuStep, setToolsMenuStep] = useState(1)
	const [topSectionLoading, setTopSectionLoading] = useState(false)
	const [bottomSectionLoading, setBottomSectionLoading] = useState(false)
	const [searchText, setSearchText] = useState('')
	const [fullTree, setFullTree] = useState(false)
	const db = useIndexedDB()
	const foremanIdRef = useRef(foremanId)
	foremanIdRef.current = foremanId
	const searchTextRef = useRef(searchText)
	searchTextRef.current = searchText
	const rootsRequest = useRef(0)
	const fetchChildren = useCallback(
		(workItemId: number) => getForemanBacklogChildren(foremanIdRef.current!, workItemId),
		[],
	)
	const tree = useLazyWorkItemTree(fetchChildren)

	useEffect(() => {
		tree.reset()
		if (foremanId) {
			setDirectReports(undefined)
			setEmployee(undefined)
			setTopSectionLoading(true)
			setBottomSectionLoading(true)
			loadData().finally(() => {
				setTopSectionLoading(false)
				setBottomSectionLoading(false)
			})
		}
	}, [foremanId])

	// A foreman's first load already applies the current search, so this only runs on edits.
	useEffect(() => {
		if (foremanId === undefined || fullTree) return
		const requestId = ++rootsRequest.current
		getForemanBacklogRoots(foremanId, searchText)
			.then((result) => {
				if (requestId !== rootsRequest.current || foremanIdRef.current !== foremanId) return
				setEmployee(result ? { ...result, workItems: result.workItems ?? [] } : undefined)
			})
			.catch(() => undefined)
	}, [searchText])

	const expandWorkItems = useCallback(
		(workItemIds: number[]) => tree.expand(workItemIds, !fullTree),
		[tree.expand, fullTree],
	)

	const setTaskStatus = async (
		changeStatusTo: boolean,
		workerId: number,
		taskId: number,
		taskType: string,
	) => {
		let result: boolean
		if (changeStatusTo) result = await activateTaskStatus(workerId, taskId, taskType, db)
		else result = await deactivateTaskStatus(workerId, taskId, taskType, db)
		await loadData()
		return result
	}

	const unassignTask = async (
		task: WorkItemTask | ReworkTask,
		fromEmployee: Employee,
		taskType: string,
	) => {
		const currentDirectReports = [...directReports!]

		setDirectReports((prevDirectReports) => {
			if (!prevDirectReports) return prevDirectReports

			return prevDirectReports.map((dr) => {
				if (dr.id === fromEmployee.id) {
					const updatedWorkItems = dr.workItems
						?.map((wi) => {
							const taskExists =
								taskType === 'reworkTask'
									? wi.reworks?.some((r) => r.id === task.id)
									: wi.tasks.some((t) => t.id === task.id)

							if (taskExists) {
								const updatedWorkItem = { ...wi }
								if (taskType === 'reworkTask') {
									updatedWorkItem.reworks = wi.reworks?.filter((r) => r.id !== task.id) ?? []
								} else {
									updatedWorkItem.tasks = wi.tasks.filter((t) => t.id !== task.id)
								}

								if (
									updatedWorkItem.tasks.length > 0 ||
									(updatedWorkItem.reworks && updatedWorkItem.reworks.length > 0)
								) {
									return updatedWorkItem
								}
								return null
							}
							return wi
						})
						.filter(Boolean) as WorkItem[]

					return {
						...dr,
						workItems: updatedWorkItems,
					}
				}
				return dr
			})
		})

		try {
			const result = await unassignTaskService(task.id, fromEmployee.id, taskType, db)
			return result
		} catch {
			setDirectReports(currentDirectReports)
			return false
		} finally {
			loadData()
		}
	}

	const assignTask = async (
		task: WorkItemTask | ReworkTask,
		toEmployee: Employee,
		taskType: string,
		workItem?: WorkItem,
	): Promise<boolean | 'duplicate'> => {
		const previousDirectReports = [...directReports!]
		const toEmployeeClone = structuredClone(toEmployee)
		const employeeWorkItems = toEmployeeClone.workItems ?? []
		const findTaskWorkItem = () => {
			const lastLayerWorkItems: WorkItem[] = []
			findLeafWorkItems(lastLayerWorkItems, employee!.workItems)
			return taskType === 'reworkTask'
				? lastLayerWorkItems.find((wi) =>
						wi.tasks.find((t) => t.reworks?.find((r) => r.id === task.id)),
				  )
				: lastLayerWorkItems.find((wi) => wi.tasks.find((t) => t.id === task.id))
		}
		// The dragged row knows its work item; the backlog may not hold the full tree.
		const taskWorkItem = workItem ?? findTaskWorkItem()
		const workItemIdx = employeeWorkItems.findIndex((wi: WorkItem) => wi.id === taskWorkItem!.id)
		if (workItemIdx > -1) {
			if (taskType === 'reworkTask') {
				if (
					employeeWorkItems
						.flatMap((wi: WorkItem) => wi.reworks ?? [])
						.some((r: ReworkTask) => r.id === task.id)
				) {
					return 'duplicate'
				}
				;(employeeWorkItems[workItemIdx].reworks ??= []).push(task as ReworkTask)
			} else {
				if (
					employeeWorkItems
						.flatMap((wi: WorkItem) => wi.tasks ?? [])
						.some((t: WorkItemTask) => t.id === task.id)
				) {
					return 'duplicate'
				}
				;(employeeWorkItems[workItemIdx].tasks ??= []).push(task as WorkItemTask)
			}
		} else {
			if (taskType === 'reworkTask') {
				employeeWorkItems.push({ ...taskWorkItem!, reworks: [task as ReworkTask], tasks: [] })
			} else {
				employeeWorkItems.push({ ...taskWorkItem!, tasks: [task as WorkItemTask], reworks: [] })
			}
		}
		setDirectReports((prev) => {
			return prev?.map((dr) => {
				if (dr.id === toEmployee.id) {
					return { ...dr, workItems: employeeWorkItems }
				}
				return dr
			})
		})
		try {
			const result = await assignTaskService(task.id, toEmployee.id, taskType, db)
			return result
		} catch {
			setDirectReports(previousDirectReports)
			return false
		}
	}

	const loadData = async () => {
		if (foremanId === undefined) return

		const requestId = ++rootsRequest.current
		const {
			employee: fetchedEmployee,
			directReports,
			fullTree: isFullTree,
		} = await getForemanBacklog(foremanId, db, searchTextRef.current)
		if (foremanIdRef.current !== foremanId) return
		setDirectReports(directReports)
		if (requestId !== rootsRequest.current) return
		setFullTree(isFullTree)
		setEmployee(
			fetchedEmployee ? { ...fetchedEmployee, workItems: fetchedEmployee.workItems ?? [] } : undefined,
		)
		// Keep open levels open with fresh data. Coming back online, they have none loaded yet.
		if (!isFullTree) tree.refreshOpen(fullTree)
	}

	const displayedEmployee = useMemo(() => {
		if (!employee) return undefined
		// Online, the server already filtered the top level by the search.
		if (!fullTree || searchText.trim() === '') return employee
		return { ...employee, workItems: searchWorkItemsHierarchy(employee.workItems ?? [], searchText) }
	}, [employee, searchText, fullTree])

	// Work items that leave the top level (search or reload) come back closed.
	const listedWorkItems = useRef<WorkItem[]>([])
	useEffect(() => {
		const workItems = displayedEmployee?.workItems ?? []
		const listedIds = new Set(workItems.map((w) => w.id))
		tree.closeUnder(listedWorkItems.current.filter((w) => !listedIds.has(w.id)))
		listedWorkItems.current = workItems
	}, [displayedEmployee?.workItems])

	async function getWorkItemPaths(workItemIds: number[]) {
		const wiHierarchy = (await getWorkItemInfo(workItemIds, db)) as WorkItemInfo[]
		return wiHierarchy
	}

	async function autoSignInWorker(directReportId: number, taskId: number, taskType: string) {
		let updatedEmployee: Employee | null = null
		setDirectReports((prevDirectReports) => {
			const updated = prevDirectReports?.map((worker) => {
				if (worker.id === directReportId) {
					updatedEmployee = { ...worker, attendanceToday: 'Present' }
					return updatedEmployee
				}
				return worker
			})
			return updated
		})

		try {
			await signInWorkerByTask(directReportId, taskType, taskId)
		} catch (error) {
			setDirectReports((prevDirectReports) =>
				prevDirectReports?.map((worker) =>
					worker.id === directReportId ? { ...worker, attendanceToday: undefined } : worker,
				),
			)
			return null
		}
		return updatedEmployee
	}

	async function revertLaborSignOut(workerId: number) {
		let updatedEmployee: Employee | null = null
		setDirectReports((prevDirectReports) => {
			const updated = prevDirectReports?.map((worker) => {
				if (worker.id === workerId) {
					updatedEmployee = { ...worker, attendanceToday: 'Present' }
					return updatedEmployee
				}
				return worker
			})
			return updated
		})

		try {
			await revertSignOut(workerId, db)
		} catch (error) {
			setDirectReports((prevDirectReports) =>
				prevDirectReports?.map((worker) =>
					worker.id === workerId ? { ...worker, attendanceToday: 'SignedOut' } : worker,
				),
			)
			return null
		}
		return updatedEmployee
	}

	return (
		<BacklogContext.Provider
			value={{
				employee: displayedEmployee,
				directReports,
				fullTree,
				expandedIds: tree.expandedIds,
				childrenById: tree.childrenById,
				loadingChildrenIds: tree.loadingIds,
				failedChildrenIds: tree.failedIds,
				toggleWorkItem: tree.toggle,
				retryChildren: tree.retry,
				expandWorkItems,
				unassignTask,
				assignTask,
				loadData,
				setTaskStatus,
				getWorkItemPaths,
				autoSignInWorker,
				revertLaborSignOut,
				openToolsMenu,
				setOpenToolsMenu,
				toolsMenuStep,
				setToolsMenuStep,
				setForemanId,
				topSectionLoading,
				bottomSectionLoading,
				searchText,
				setSearchText,
			}}
		>
			{children}
		</BacklogContext.Provider>
	)
}

export { useBacklogContext, BacklogProvider }
